/**
 * Cat Related Pages
 * Cat listings and individual cat detail pages with adoption application
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Cat, Heart, Mail, Gift, ChevronRight, Clock, MapPin, Phone, ExternalLink } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";

import { API } from "../lib/api";

const CatsPage = () => {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [interestForm, setInterestForm] = useState({
    applicant_names: "",
    email: "",
    phone: "",
    address: "",
    property_size: "",
    number_of_rooms: "",
    near_main_road: false,
    road_number: "",
    indoor_outdoor: "",
    has_other_pets: false,
    other_pets_dogs: false,
    other_pets_cats: false,
    other_pets_exotics: false,
    other_pets_fish: false,
    other_pets_other: false,
    other_pets_details: "",
    has_children: false,
    children_ages: "",
    previous_experience: "",
    cat_preferences: "",
    what3words: "",
    additional_info: ""
  });

  useEffect(() => {
    axios.get(`${API}/cats?adopted=false`)
      .then(r => setCats(r.data.filter(c => !c.is_adopted)))
      .catch(() => toast.error("Failed to load cats"))
      .finally(() => setLoading(false));
  }, []);

  const handleInterestSubmit = async () => {
    const required = ["applicant_names", "email", "phone", "address", "property_size", "number_of_rooms", "indoor_outdoor", "previous_experience"];
    for (const field of required) {
      if (!interestForm[field]) {
        toast.error(`Please fill in ${field.replace(/_/g, " ")}`);
        return;
      }
    }
    
    // Build other pets details string from checkboxes
    const petTypes = [];
    if (interestForm.other_pets_dogs) petTypes.push("Dogs");
    if (interestForm.other_pets_cats) petTypes.push("Cats");
    if (interestForm.other_pets_exotics) petTypes.push("Exotics");
    if (interestForm.other_pets_fish) petTypes.push("Fish");
    if (interestForm.other_pets_other) petTypes.push("Other");
    const otherPetsDescription = petTypes.length > 0 
      ? `${petTypes.join(", ")}${interestForm.other_pets_details ? ` - ${interestForm.other_pets_details}` : ""}`
      : interestForm.other_pets_details;
    
    // Prepare submission data
    const submitData = {
      ...interestForm,
      other_pets_details: otherPetsDescription,
      children_details: interestForm.children_ages,
      additional_info: `${interestForm.additional_info}${interestForm.near_main_road ? `\nNear main road: Yes (Road: ${interestForm.road_number || 'Not specified'})` : ''}${interestForm.what3words ? `\nWhat3Words: ${interestForm.what3words}` : ''}`.trim()
    };
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/adoptions/general-interest`, submitData);
      toast.success("Your interest has been registered! We'll be in touch when suitable cats become available.");
      setShowInterestForm(false);
      setInterestForm({
        applicant_names: "", email: "", phone: "", address: "", property_size: "",
        number_of_rooms: "", near_main_road: false, road_number: "",
        indoor_outdoor: "", has_other_pets: false, other_pets_dogs: false,
        other_pets_cats: false, other_pets_exotics: false, other_pets_fish: false,
        other_pets_other: false, other_pets_details: "", has_children: false,
        children_ages: "", previous_experience: "", cat_preferences: "",
        what3words: "", additional_info: ""
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const availableCats = cats.filter(c => !c.is_adopted);

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="cats-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Cats Looking for Homes</h1>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Every cat here has been rescued, health-checked, and is ready to find their forever family. 
            Could you be the one?
          </p>
        </div>

        {/* Adoption Fees Section */}
        <div className="max-w-3xl mx-auto mb-12" data-testid="adoption-fees-section">
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 overflow-hidden">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-amber-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-stone-900">Adoption Fees</h2>
              </div>
              <p className="text-stone-600 mb-6">
                To keep our rescue running costs to a minimum, we charge the following adoption fees:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white/70 rounded-xl p-4 border border-amber-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-stone-900">Adult Cats</p>
                      <p className="text-sm text-stone-500">1 year of age or over</p>
                    </div>
                    <span className="text-2xl font-bold text-amber-600">£80</span>
                  </div>
                </div>
                <div className="bg-white/70 rounded-xl p-4 border border-amber-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-stone-900">Kittens</p>
                      <p className="text-sm text-stone-500">Under 1 year of age</p>
                    </div>
                    <span className="text-2xl font-bold text-amber-600">£100</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : availableCats.length === 0 ? (
          /* All Cats Adopted Message */
          <Card className="max-w-2xl mx-auto text-center p-12 bg-gradient-to-br from-amber-50 to-stone-50 border-amber-200">
            <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-amber-600 fill-amber-600" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4">
              Wonderful News!
            </h2>
            <p className="text-lg text-stone-700 mb-6 leading-relaxed">
              All of the cats that needed new loving homes have been adopted! 
            </p>
            <p className="text-stone-600 mb-8">
              We will update this page when more of our <span className="font-semibold text-amber-600">Fur Family</span> are available to adopt.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/contact")} className="bg-amber-600 hover:bg-amber-700 rounded-full px-8">
                <Mail className="w-4 h-4 mr-2" /> Get Notified
              </Button>
              <Button onClick={() => navigate("/donate")} variant="outline" className="rounded-full px-8 border-amber-300 hover:bg-amber-50">
                <Gift className="w-4 h-4 mr-2" /> Support Our Cats
              </Button>
            </div>
          </Card>
        ) : (
          /* Available Cats Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {availableCats.map(cat => (
              <Card key={cat.cat_id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate(`/cats/${cat.cat_id}`)}>
                <div className="aspect-[4/3] relative overflow-hidden">
                  {(cat.image_base64 || cat.image_url) ? (
                    <>
                      <img 
                        src={cat.image_base64 ? `data:${cat.image_mime_type || 'image/jpeg'};base64,${cat.image_base64}` : cat.image_url} 
                        alt={cat.name} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-gradient-to-br from-amber-100 to-stone-100 items-center justify-center hidden absolute inset-0">
                        <Cat className="w-16 h-16 text-amber-300" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-amber-100 to-stone-100 flex items-center justify-center">
                      <Cat className="w-16 h-16 text-amber-300" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-amber-600 text-white">{cat.gender}</Badge>
                  </div>
                  {/* Reserved Banner for public page */}
                  {cat.is_reserved && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="absolute inset-0 bg-black/30" />
                      <div className="relative bg-red-600 text-white px-6 py-2 font-bold text-lg transform -rotate-12 shadow-lg">
                        RESERVED
                      </div>
                    </div>
                  )}
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-stone-900 mb-1">{cat.name}</h3>
                  <p className="text-amber-600 font-medium mb-2">{cat.breed} • {cat.age}</p>
                  <p className="text-stone-600 text-sm line-clamp-2">{cat.description}</p>
                  {cat.personality && cat.personality.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {cat.personality.slice(0, 3).map(trait => (
                        <Badge key={trait} variant="outline" className="text-xs border-amber-200 text-amber-700">{trait}</Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Register Interest Section */}
        <div className="mt-12" data-testid="register-interest-section">
          <Card className="max-w-2xl mx-auto bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-6 md:p-8 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-2">Interested in Adopting?</h3>
              <p className="text-stone-600 mb-6">
                Register your interest to be added to our waiting list. We'll vet your application and contact you when suitable cats become available.
              </p>
              <Button 
                onClick={() => setShowInterestForm(true)} 
                className="bg-green-600 hover:bg-green-700 rounded-full px-8"
                data-testid="register-interest-btn"
              >
                <Heart className="w-4 h-4 mr-2" /> Register Your Interest
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* General Interest Registration Form Dialog */}
        <Dialog open={showInterestForm} onOpenChange={setShowInterestForm}>
          <DialogContent className="max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="text-2xl">Register Adoption Interest</DialogTitle>
              <DialogDescription>
                Fill out this form to be added to our waiting list. We'll review your application and contact you when suitable cats become available.
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4 py-4">
                {/* Personal Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name(s) *</Label>
                    <Input 
                      placeholder="Names of all household members" 
                      value={interestForm.applicant_names}
                      onChange={e => setInterestForm({...interestForm, applicant_names: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email" 
                      placeholder="your@email.com"
                      value={interestForm.email}
                      onChange={e => setInterestForm({...interestForm, email: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input 
                    placeholder="Your phone number"
                    value={interestForm.phone}
                    onChange={e => setInterestForm({...interestForm, phone: e.target.value})}
                  />
                </div>

                {/* Property Details */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-stone-900 mb-3">Property Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Property Size *</Label>
                      <Select value={interestForm.property_size} onValueChange={v => setInterestForm({...interestForm, property_size: v})}>
                        <SelectTrigger><SelectValue placeholder="Select property size" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Small flat/apartment">Small flat/apartment</SelectItem>
                          <SelectItem value="Medium house">Medium house</SelectItem>
                          <SelectItem value="Large house">Large house</SelectItem>
                          <SelectItem value="House with garden">House with garden</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Number of Rooms *</Label>
                      <Input 
                        placeholder="e.g., 3 bedrooms, 1 living room"
                        value={interestForm.number_of_rooms}
                        onChange={e => setInterestForm({...interestForm, number_of_rooms: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label>Full Address *</Label>
                    <Textarea 
                      placeholder="Your full address including postcode"
                      value={interestForm.address}
                      onChange={e => setInterestForm({...interestForm, address: e.target.value})}
                    />
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        id="near_main_road"
                        checked={interestForm.near_main_road}
                        onChange={e => setInterestForm({...interestForm, near_main_road: e.target.checked})}
                        className="rounded border-stone-300"
                      />
                      <Label htmlFor="near_main_road">Do you live near a main road?</Label>
                    </div>
                    {interestForm.near_main_road && (
                      <Input 
                        placeholder="Road number/name (e.g., A47, B1108)"
                        value={interestForm.road_number}
                        onChange={e => setInterestForm({...interestForm, road_number: e.target.value})}
                      />
                    )}
                  </div>
                </div>

                {/* Cat Environment */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-stone-900 mb-3">Cat Environment</h4>
                  <div>
                    <Label>Indoor/Outdoor Preference *</Label>
                    <Select value={interestForm.indoor_outdoor} onValueChange={v => setInterestForm({...interestForm, indoor_outdoor: v})}>
                      <SelectTrigger><SelectValue placeholder="Select preference" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Indoor only">Indoor only</SelectItem>
                        <SelectItem value="Outdoor only">Outdoor only</SelectItem>
                        <SelectItem value="Indoor with outdoor access">Indoor with outdoor access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Other Pets Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-stone-900 mb-3">Other Pets</h4>
                  <div className="flex items-center gap-2 mb-3">
                    <input 
                      type="checkbox" 
                      id="has_other_pets"
                      checked={interestForm.has_other_pets}
                      onChange={e => setInterestForm({...interestForm, has_other_pets: e.target.checked})}
                      className="rounded border-stone-300"
                    />
                    <Label htmlFor="has_other_pets">Do you have other pets?</Label>
                  </div>
                  {interestForm.has_other_pets && (
                    <div className="space-y-3 ml-6">
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            id="other_pets_dogs"
                            checked={interestForm.other_pets_dogs}
                            onChange={e => setInterestForm({...interestForm, other_pets_dogs: e.target.checked})}
                            className="rounded border-stone-300"
                          />
                          <Label htmlFor="other_pets_dogs" className="font-normal">Dogs</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            id="other_pets_cats"
                            checked={interestForm.other_pets_cats}
                            onChange={e => setInterestForm({...interestForm, other_pets_cats: e.target.checked})}
                            className="rounded border-stone-300"
                          />
                          <Label htmlFor="other_pets_cats" className="font-normal">Cats</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            id="other_pets_exotics"
                            checked={interestForm.other_pets_exotics}
                            onChange={e => setInterestForm({...interestForm, other_pets_exotics: e.target.checked})}
                            className="rounded border-stone-300"
                          />
                          <Label htmlFor="other_pets_exotics" className="font-normal">Exotics</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            id="other_pets_fish"
                            checked={interestForm.other_pets_fish}
                            onChange={e => setInterestForm({...interestForm, other_pets_fish: e.target.checked})}
                            className="rounded border-stone-300"
                          />
                          <Label htmlFor="other_pets_fish" className="font-normal">Fish</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            id="other_pets_other"
                            checked={interestForm.other_pets_other}
                            onChange={e => setInterestForm({...interestForm, other_pets_other: e.target.checked})}
                            className="rounded border-stone-300"
                          />
                          <Label htmlFor="other_pets_other" className="font-normal">Other</Label>
                        </div>
                      </div>
                      <Input 
                        placeholder="Please provide details about your pets (breeds, ages, etc.)"
                        value={interestForm.other_pets_details}
                        onChange={e => setInterestForm({...interestForm, other_pets_details: e.target.value})}
                      />
                    </div>
                  )}
                </div>

                {/* Children Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-stone-900 mb-3">Children</h4>
                  <div className="flex items-center gap-2 mb-3">
                    <input 
                      type="checkbox" 
                      id="has_children"
                      checked={interestForm.has_children}
                      onChange={e => setInterestForm({...interestForm, has_children: e.target.checked})}
                      className="rounded border-stone-300"
                    />
                    <Label htmlFor="has_children">Do you have children?</Label>
                  </div>
                  {interestForm.has_children && (
                    <div className="ml-6">
                      <Label>Ages of Children</Label>
                      <Input 
                        placeholder="e.g., 5 years, 8 years, 12 years"
                        value={interestForm.children_ages}
                        onChange={e => setInterestForm({...interestForm, children_ages: e.target.value})}
                      />
                    </div>
                  )}
                </div>

                {/* Experience Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-stone-900 mb-3">Experience & Preferences</h4>
                  <div className="space-y-4">
                    <div>
                      <Label>Previous Cat Experience *</Label>
                      <Textarea 
                        placeholder="Tell us about your experience with cats"
                        value={interestForm.previous_experience}
                        onChange={e => setInterestForm({...interestForm, previous_experience: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Cat Preferences (Optional)</Label>
                      <Textarea 
                        placeholder="Any preferences for the type of cat? (e.g., age, temperament, breed)"
                        value={interestForm.cat_preferences}
                        onChange={e => setInterestForm({...interestForm, cat_preferences: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Additional Information (Optional)</Label>
                      <Textarea 
                        placeholder="Anything else you'd like us to know?"
                        value={interestForm.additional_info}
                        onChange={e => setInterestForm({...interestForm, additional_info: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* What3Words Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-stone-900 mb-2">What3Words Location</h4>
                  <p className="text-sm text-stone-500 mb-3">
                    Help us find your exact location using What3Words - a simple way to identify precise locations.
                  </p>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="e.g., ///filled.count.soap"
                      value={interestForm.what3words}
                      onChange={e => setInterestForm({...interestForm, what3words: e.target.value})}
                      className="flex-1"
                    />
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => window.open('https://what3words.com/', '_blank')}
                      className="shrink-0"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" /> Find Location
                    </Button>
                  </div>
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInterestForm(false)}>Cancel</Button>
              <Button 
                onClick={handleInterestSubmit} 
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

const CatDetailPage = () => {
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdoptionForm, setShowAdoptionForm] = useState(false);
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [adoptionForm, setAdoptionForm] = useState({
    applicant_names: "",
    email: "",
    phone: "",
    address: "",
    property_size: "",
    indoor_outdoor: "",
    has_other_pets: false,
    other_pets_details: "",
    has_children: false,
    children_details: "",
    previous_experience: "",
    additional_info: ""
  });
  const [waitlistForm, setWaitlistForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const id = location.pathname.split("/").pop();
    axios.get(`${API}/cats/${id}`)
      .then(r => setCat(r.data))
      .catch(() => { toast.error("Cat not found"); navigate("/cats"); })
      .finally(() => setLoading(false));
  }, [location.pathname, navigate]);

  const handleWaitlistSubmit = async () => {
    if (!waitlistForm.name || !waitlistForm.email) {
      toast.error("Please fill in name and email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/waitlist`, {
        cat_id: cat.cat_id,
        cat_name: cat.name,
        ...waitlistForm
      });
      toast.success(`You're #${res.data.position} on the waiting list! We'll email you if ${cat.name} becomes available.`);
      setShowWaitlistForm(false);
      setWaitlistForm({ name: "", email: "", phone: "", notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join waiting list");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdoptionSubmit = async () => {
    // Validate required fields
    const required = ["applicant_names", "email", "phone", "address", "property_size", "indoor_outdoor", "previous_experience"];
    for (const field of required) {
      if (!adoptionForm[field]) {
        toast.error(`Please fill in ${field.replace(/_/g, " ")}`);
        return;
      }
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/adoptions`, {
        cat_id: cat.cat_id,
        cat_name: cat.name,
        ...adoptionForm
      });
      toast.success("Adoption application submitted! We'll be in touch soon.");
      setShowAdoptionForm(false);
      setAdoptionForm({
        applicant_names: "", email: "", phone: "", address: "", property_size: "",
        indoor_outdoor: "", has_other_pets: false, other_pets_details: "",
        has_children: false, children_details: "", previous_experience: "", additional_info: ""
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen pt-36 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full" /></div>;
  if (!cat) return null;

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="cat-detail-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <Button variant="ghost" onClick={() => navigate("/cats")} className="mb-6" data-testid="back-btn">
          <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back to all cats
        </Button>
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-4">
            {(cat.image_base64 || cat.image_url) ? (
              <img src={cat.image_base64 ? `data:${cat.image_mime_type || 'image/jpeg'};base64,${cat.image_base64}` : cat.image_url} alt={cat.name} className="w-full aspect-square object-cover rounded-2xl shadow-lg" />
            ) : (
              <div className="w-full aspect-square bg-gradient-to-br from-amber-100 to-stone-100 flex items-center justify-center rounded-2xl shadow-lg">
                <Cat className="w-32 h-32 text-amber-300" />
              </div>
            )}
            {cat.is_reserved && (
              <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-center">
                <Badge className="bg-red-600 text-white mb-2">RESERVED</Badge>
                <p className="text-red-700 text-sm">This cat is currently reserved for another adopter.</p>
              </div>
            )}
          </div>
          <div>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-4xl font-bold text-stone-900 mb-2">{cat.name}</h1>
                <p className="text-lg text-amber-600 font-medium">{cat.breed}</p>
              </div>
              <Badge className="bg-amber-100 text-amber-700 text-lg px-4 py-1">{cat.gender}</Badge>
            </div>
            <div className="flex items-center gap-4 mb-6 text-stone-600">
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {cat.age}</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> Norwich, Norfolk</span>
            </div>
            <p className="text-stone-700 leading-relaxed mb-6">{cat.description}</p>
            <div className="mb-8">
              <h3 className="font-semibold text-stone-900 mb-3">Personality</h3>
              <div className="flex flex-wrap gap-2">
                {cat.personality?.map(p => (
                  <Badge key={p} className="bg-green-100 text-green-700 px-3 py-1">{p}</Badge>
                ))}
              </div>
            </div>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-stone-900 mb-2">Interested in adopting {cat.name}?</h3>
                <p className="text-stone-600 text-sm mb-4">
                  {cat.is_reserved 
                    ? "This cat is currently reserved, but you can join the waiting list to be notified if they become available!"
                    : "Fill out our adoption application form to start the process!"}
                </p>
                <div className="flex flex-wrap gap-3">
                  {!cat.is_reserved && !cat.is_adopted && (
                    <Button 
                      onClick={() => setShowAdoptionForm(true)} 
                      className="bg-amber-600 hover:bg-amber-700 rounded-full" 
                      data-testid="apply-adopt-btn"
                    >
                      <Heart className="w-4 h-4 mr-2" /> Apply to Adopt {cat.name}
                    </Button>
                  )}
                  {cat.is_reserved && !cat.is_adopted && (
                    <Button 
                      onClick={() => setShowWaitlistForm(true)} 
                      className="bg-green-600 hover:bg-green-700 rounded-full" 
                      data-testid="join-waitlist-btn"
                    >
                      <Clock className="w-4 h-4 mr-2" /> Join Waiting List
                    </Button>
                  )}
                  <Button 
                    onClick={() => navigate("/contact")} 
                    variant="outline" 
                    className="rounded-full" 
                    data-testid="contact-about-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" /> Contact Us
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Waiting List Dialog */}
      <Dialog open={showWaitlistForm} onOpenChange={setShowWaitlistForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Join Waiting List for {cat.name}</DialogTitle>
            <DialogDescription>
              {cat.name} is currently reserved. Join our waiting list and we'll notify you if they become available!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="wl-name">Your Name *</Label>
              <Input id="wl-name" value={waitlistForm.name} onChange={e => setWaitlistForm({...waitlistForm, name: e.target.value})} placeholder="Full name" />
            </div>
            <div>
              <Label htmlFor="wl-email">Email *</Label>
              <Input id="wl-email" type="email" value={waitlistForm.email} onChange={e => setWaitlistForm({...waitlistForm, email: e.target.value})} placeholder="your@email.com" />
            </div>
            <div>
              <Label htmlFor="wl-phone">Phone (optional)</Label>
              <Input id="wl-phone" value={waitlistForm.phone} onChange={e => setWaitlistForm({...waitlistForm, phone: e.target.value})} placeholder="07xxx xxx xxx" />
            </div>
            <div>
              <Label htmlFor="wl-notes">Notes (optional)</Label>
              <Textarea id="wl-notes" value={waitlistForm.notes} onChange={e => setWaitlistForm({...waitlistForm, notes: e.target.value})} placeholder="Any additional information..." rows={2} />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowWaitlistForm(false)}>Cancel</Button>
            <Button onClick={handleWaitlistSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">
              {submitting ? "Joining..." : "Join Waiting List"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adoption Application Dialog */}
      <Dialog open={showAdoptionForm} onOpenChange={setShowAdoptionForm}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Adoption Application for {cat.name}</DialogTitle>
            <DialogDescription>
              Please fill out this form to apply to adopt {cat.name}. We'll review your application and get back to you soon!
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="app-names">Name(s) of household members *</Label>
                <Input id="app-names" value={adoptionForm.applicant_names} onChange={e => setAdoptionForm({...adoptionForm, applicant_names: e.target.value})} placeholder="All adults in household" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="app-email">Email *</Label>
                  <Input id="app-email" type="email" value={adoptionForm.email} onChange={e => setAdoptionForm({...adoptionForm, email: e.target.value})} placeholder="your@email.com" />
                </div>
                <div>
                  <Label htmlFor="app-phone">Phone *</Label>
                  <Input id="app-phone" value={adoptionForm.phone} onChange={e => setAdoptionForm({...adoptionForm, phone: e.target.value})} placeholder="07xxx xxx xxx" />
                </div>
              </div>
              <div>
                <Label htmlFor="app-address">Full Address *</Label>
                <Textarea id="app-address" value={adoptionForm.address} onChange={e => setAdoptionForm({...adoptionForm, address: e.target.value})} placeholder="House number, street, city, postcode" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="app-property">Property Size *</Label>
                  <Select value={adoptionForm.property_size} onValueChange={v => setAdoptionForm({...adoptionForm, property_size: v})}>
                    <SelectTrigger id="app-property"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat/Apartment</SelectItem>
                      <SelectItem value="small_house">Small House</SelectItem>
                      <SelectItem value="medium_house">Medium House</SelectItem>
                      <SelectItem value="large_house">Large House</SelectItem>
                      <SelectItem value="farm">Farm/Rural Property</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="app-indoor">Indoor/Outdoor Plan *</Label>
                  <Select value={adoptionForm.indoor_outdoor} onValueChange={v => setAdoptionForm({...adoptionForm, indoor_outdoor: v})}>
                    <SelectTrigger id="app-indoor"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indoor_only">Indoor Only</SelectItem>
                      <SelectItem value="indoor_with_catio">Indoor with Catio/Enclosed Garden</SelectItem>
                      <SelectItem value="indoor_outdoor">Indoor/Outdoor Access</SelectItem>
                      <SelectItem value="outdoor_primarily">Primarily Outdoor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="app-pets" checked={adoptionForm.has_other_pets} onChange={e => setAdoptionForm({...adoptionForm, has_other_pets: e.target.checked})} className="rounded" />
                  <Label htmlFor="app-pets">Do you have other pets?</Label>
                </div>
                {adoptionForm.has_other_pets && (
                  <Textarea value={adoptionForm.other_pets_details} onChange={e => setAdoptionForm({...adoptionForm, other_pets_details: e.target.value})} placeholder="Please describe your other pets (type, age, temperament)" rows={2} />
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="app-children" checked={adoptionForm.has_children} onChange={e => setAdoptionForm({...adoptionForm, has_children: e.target.checked})} className="rounded" />
                  <Label htmlFor="app-children">Do you have children?</Label>
                </div>
                {adoptionForm.has_children && (
                  <Textarea value={adoptionForm.children_details} onChange={e => setAdoptionForm({...adoptionForm, children_details: e.target.value})} placeholder="Please provide ages of children" rows={2} />
                )}
              </div>
              <div>
                <Label htmlFor="app-exp">Previous experience as cat custodians *</Label>
                <Textarea id="app-exp" value={adoptionForm.previous_experience} onChange={e => setAdoptionForm({...adoptionForm, previous_experience: e.target.value})} placeholder="Tell us about your experience with cats..." rows={3} />
              </div>
              <div>
                <Label htmlFor="app-info">Additional information</Label>
                <Textarea id="app-info" value={adoptionForm.additional_info} onChange={e => setAdoptionForm({...adoptionForm, additional_info: e.target.value})} placeholder="Anything else you'd like us to know?" rows={3} />
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAdoptionForm(false)}>Cancel</Button>
            <Button onClick={handleAdoptionSubmit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
              {submitting ? "Submitting..." : "Submit Application"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { CatsPage, CatDetailPage };
